import { useState } from "react";
import "./reports.scss";
import reportFile from "../../assets/script.pdf";
import { MdClose, MdSearch, MdVisibility } from "react-icons/md";
import { Button, TextField, InputAdornment } from "@mui/material";
import * as XLSX from "xlsx";

// Add this CSS at the top of your file after the imports


function Report() {
  
  const [step6Files, setStep6Files] = useState([]);
  const [showCards, setShowCards] = useState(false);
  const [selectedReport, setSelectedReport] = useState("efficiency");
  const [currentPage, setCurrentPage] = useState(1);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [hitPage, setHitPage] = useState(1);
  const [unhitPage, setUnhitPage] = useState(1);
  const [scenarioPage, setScenarioPage] = useState(1);
  const itemsPerPage = 5;

  // Dummy data for scenario summary
  const scenarioSummaryData = [
    {
      id: 1,
      scenarioName: "Run Together",
      totalRecords: 1000,
      hitCount: 150,
      hitPercentage: 15,
      unhitCount: 850,
      unhitPercentage: 85,
      details: {
        hits: [
          {
            name: "John Smith",
            processedName: "JOHNSMITH",
            violations: [
              "Name without spaces: JOHNSMITH",
              "Single word format: JOHNSMITH",
              "No word separation: JOHNSMITH",
            ],
          },
          {
            name: "Sarah Johnson",
            processedName: "SARAHJOHNSON",
            violations: [
              "Name without spaces: SARAHJOHNSON",
              "Single word format: SARAHJOHNSON",
              "No word separation: SARAHJOHNSON",
            ],
          },
          {
            name: "Michael Brown",
            processedName: "MICHAELBROWN",
            violations: [
              "Name without spaces: MICHAELBROWN",
              "Single word format: MICHAELBROWN",
              "No word separation: MICHAELBROWN",
            ],
          },
        ],
        unhits: [
          {
            name: "David Wilson",
            processedName: "DAVID WILSON",
            variations: [
              "Original format: DAVID WILSON",
              "Two word format: DAVID WILSON",
              "Proper spacing: DAVID WILSON",
            ],
          },
          {
            name: "Emma Davis",
            processedName: "EMMA DAVIS",
            variations: [
              "Original format: EMMA DAVIS",
              "Two word format: EMMA DAVIS",
              "Proper spacing: EMMA DAVIS",
            ],
          },
          {
            name: "James Miller",
            processedName: "JAMES MILLER",
            variations: [
              "Original format: JAMES MILLER",
              "Two word format: JAMES MILLER",
              "Proper spacing: JAMES MILLER",
            ],
          },
        ],
      },
    },
    {
      id: 2,
      scenarioName: "Abbreviations",
      totalRecords: 800,
      hitCount: 80,
      hitPercentage: 10,
      unhitCount: 720,
      unhitPercentage: 90,
      details: {
        hits: [
          {
            name: "Robert Taylor",
            processedName: "ROB TAYLOR",
            violations: [
              "Abbreviated first name: ROB TAYLOR",
              "Shortened format: ROB TAYLOR",
              "Initial format: R TAYLOR",
            ],
          },
          {
            name: "Lisa Anderson",
            processedName: "L ANDERSON",
            violations: [
              "Initial only: L ANDERSON",
              "Single letter format: L ANDERSON",
              "Abbreviated format: L ANDERSON",
            ],
          },
          {
            name: "Thomas White",
            processedName: "TOM WHITE",
            violations: [
              "Shortened first name: TOM WHITE",
              "Nickname format: TOM WHITE",
              "Abbreviated format: T WHITE",
            ],
          },
        ],
        unhits: [
          {
            name: "Jennifer Lee",
            processedName: "JENNIFER LEE",
            variations: [
              "Full name format: JENNIFER LEE",
              "Complete format: JENNIFER LEE",
              "Proper format: JENNIFER LEE",
            ],
          },
          {
            name: "William Clark",
            processedName: "WILLIAM CLARK",
            variations: [
              "Full name format: WILLIAM CLARK",
              "Complete format: WILLIAM CLARK",
              "Proper format: WILLIAM CLARK",
            ],
          },
          {
            name: "Mary Hall",
            processedName: "MARY HALL",
            variations: [
              "Full name format: MARY HALL",
              "Complete format: MARY HALL",
              "Proper format: MARY HALL",
            ],
          },
        ],
      },
    },
    {
      id: 3,
      scenarioName: "Aliases",
      totalRecords: 1200,
      hitCount: 240,
      hitPercentage: 20,
      unhitCount: 960,
      unhitPercentage: 80,
      details: {
        hits: [
          {
            name: "Daniel Moore",
            processedName: "DANNY MOORE",
            violations: [
              "Nickname used: DANNY MOORE",
              "Alternative name: DANNY MOORE",
              "Common alias: DANNY MOORE",
            ],
          },
          {
            name: "Patricia Lewis",
            processedName: "PAT LEWIS",
            violations: [
              "Shortened name: PAT LEWIS",
              "Nickname format: PAT LEWIS",
              "Alternative format: PAT LEWIS",
            ],
          },
          {
            name: "Christopher Walker",
            processedName: "CHRIS WALKER",
            violations: [
              "Common nickname: CHRIS WALKER",
              "Shortened format: CHRIS WALKER",
              "Alternative name: CHRIS WALKER",
            ],
          },
        ],
        unhits: [
          {
            name: "Elizabeth Young",
            processedName: "ELIZABETH YOUNG",
            variations: [
              "Full name format: ELIZABETH YOUNG",
              "Complete format: ELIZABETH YOUNG",
              "Proper format: ELIZABETH YOUNG",
            ],
          },
          {
            name: "Matthew Allen",
            processedName: "MATTHEW ALLEN",
            variations: [
              "Full name format: MATTHEW ALLEN",
              "Complete format: MATTHEW ALLEN",
              "Proper format: MATTHEW ALLEN",
            ],
          },
          {
            name: "Nancy King",
            processedName: "NANCY KING",
            variations: [
              "Full name format: NANCY KING",
              "Complete format: NANCY KING",
              "Proper format: NANCY KING",
            ],
          },
        ],
      },
    },
  ];

  const [selectedHit, setSelectedHit] = useState(null);
  const [selectedUnhit, setSelectedUnhit] = useState(null);

  const handleHitClick = (hit) => {
    setSelectedHit(hit);
  };

  // Add pagination functions
  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(Math.ceil(10 / itemsPerPage)); // 10 is the total number of rows in the table
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(10 / itemsPerPage)));
  };

  const handleReportSelect = (report) => {
    setSelectedReport(report);
  };

  const handleStep6FileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      
      // const allowedFileTypes = [
      //     "application/pdf",
      //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
      //     "application/vnd.ms-excel", // XLS
      //     "text/csv", // CSV
      //     "application/csv" // Some browsers may use this MIME type for CSV
      // ];

      // if (allowedFileTypes.includes(fileType)) {
      setStep6Files([...step6Files, selectedFile]);
      // } else {
      //     Swal.fire({
      //         title: "Invalid File Format",
      //         text: "Only PDF, XLSX and CSV file formats are allowed!",
      //         icon: "warning",
      //         confirmButtonText: "Ok",
      //     });
      // }
    }
  };

  const handleDownload = (title) => {
    const link = document.createElement("a");
    link.href = reportFile;
    link.download = `${title}.pdf`; // Set the filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportXLSX = () => {
    // Get the table element
    const table = document.getElementById("fileTable");
    if (!table) {
      console.warn("Table not found");
      return;
    }

    // Extract headers
    const headers = [];
    const headerRow = table.querySelector("thead tr");
    if (headerRow) {
      headerRow.querySelectorAll("th").forEach((th) => {
        headers.push(th.textContent);
      });
    }

    // Extract data rows
    const rows = [];
    table.querySelectorAll("tbody tr").forEach((tr) => {
      const rowData = [];
      tr.querySelectorAll("td").forEach((td) => {
        rowData.push(td.textContent);
      });
      rows.push(rowData);
    });

    // Create the sheet data with headers followed by rows
    const sheetData = [headers, ...rows];

    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Table Data");

    // Generate file name with current date

    const fileName = `report_master.xlsx`;

    // Export file
    XLSX.writeFile(wb, fileName);
  };

  const handleCloseScenarioModal = () => {
    setShowScenarioModal(false);
    setSelectedScenario(null);
  };

  // Add pagination functions for hits
  const handleHitFirstPage = () => {
    setHitPage(1);
  };

  const handleHitLastPage = () => {
    setHitPage(Math.ceil(selectedScenario?.details.hits.length / itemsPerPage));
  };

  const handleHitPrevPage = () => {
    setHitPage((prev) => Math.max(prev - 1, 1));
  };

  const handleHitNextPage = () => {
    setHitPage((prev) =>
      Math.min(
        prev + 1,
        Math.ceil(selectedScenario?.details.hits.length / itemsPerPage)
      )
    );
  };

  // Add pagination functions for unhits
  const handleUnhitFirstPage = () => {
    setUnhitPage(1);
  };

  const handleUnhitLastPage = () => {
    setUnhitPage(
      Math.ceil(selectedScenario?.details.unhits.length / itemsPerPage)
    );
  };

  const handleUnhitPrevPage = () => {
    setUnhitPage((prev) => Math.max(prev - 1, 1));
  };

  const handleUnhitNextPage = () => {
    setUnhitPage((prev) =>
      Math.min(
        prev + 1,
        Math.ceil(selectedScenario?.details.unhits.length / itemsPerPage)
      )
    );
  };

  // Add pagination functions for scenario summary
  const handleScenarioFirstPage = () => {
    setScenarioPage(1);
  };

  const handleScenarioLastPage = () => {
    setScenarioPage(Math.ceil(scenarioSummaryData.length / itemsPerPage));
  };

  const handleScenarioPrevPage = () => {
    setScenarioPage((prev) => Math.max(prev - 1, 1));
  };

  const handleScenarioNextPage = () => {
    setScenarioPage((prev) =>
      Math.min(prev + 1, Math.ceil(scenarioSummaryData.length / itemsPerPage))
    );
  };

  const [viewType, setViewType] = useState(null);
  const handleScenarioClick = (scenario, type = null) => {
    setSelectedScenario(scenario);
    setSelectedHit(null);
    setSelectedUnhit(null);

    if (type === "hits") {
      setViewType("hits");
    } else if (type === "unhits") {
      setViewType("unhits");
    } else {
      setViewType(null);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredHits = selectedScenario?.details.hits.filter((hit) =>
    hit.name.toLowerCase().includes(searchTerm)
  );


  // For View Files Modal
    const [viewFilesModal, setViewFilesModal] = useState(false);
    const [modalCurrentPage, setModalCurrentPage] = useState(1);
    const modalItemsPerPage = 5;
   
    
    const handleModalFirstPage = () => setModalCurrentPage(1);
    const handleModalLastPage = () => setModalCurrentPage(Math.ceil(staticFileList.length / modalItemsPerPage));
    const handleModalPrevPage = () => setModalCurrentPage((prev) => Math.max(prev - 1, 1));
    const handleModalNextPage = () => setModalCurrentPage((prev) => Math.min(prev + 1, Math.ceil(staticFileList.length / modalItemsPerPage)));


    const staticFileList = [
        { id: 1, fileName: "example-file-1.pdf", loadDate: "21-05-2025", loadTime: "10:00:00" },
        { id: 2, fileName: "example-file-2.pdf", loadDate: "20-05-2025", loadTime: "11:30:00" },
        { id: 3, fileName: "example-file-3.pdf", loadDate: "19-05-2025", loadTime: "09:15:00" },
    ];
  

  return (
    <div className="efficiency-page">
      <div className="page-container">
        <div className="row justify-content-center">
          <div className="col-12">
            <h5 className="mb-4">Efficiency Report</h5>
          </div>
        </div>
        <div className="row">
            <div className="col-12">
                <div>
                    <button
                        className="anchor-button"
                        onClick={() => setViewFilesModal(true)}
                    >
                        View Files
                    </button>
                </div>
                <div className="row align-items-end">
                <div className="col-12 col-md-6">
                    <div className="min-width">
                    <TextField
                        fullWidth
                        disabled
                        label="Output File"
                        value={`outputfile_${
                        new Date().toISOString().split("T")[0]
                        }_${new Date()
                        .toLocaleTimeString()
                        .replace(/:/g, "-")}.pdf`}
                        variant="outlined"
                    />
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <h6 className="">Alert File</h6>
                    <div className="choose-file-input d-flex align-items-center mt-2">
                    <label
                        className="btn choose-file-btn me-2"
                        htmlFor="step6FileInput"
                    >
                        Choose File
                    </label>
                    <input
                        id="step6FileInput"
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleStep6FileChange}
                    />
                    <p
                        className="mb-0 choose-file-name flex-grow-1"
                        onClick={() =>
                        document.getElementById("step6FileInput").click()
                        }
                    >
                        {step6Files.length > 0
                        ? step6Files[step6Files.length - 1].name
                        : "No file chosen"}
                    </p>
                    </div>
                </div>
                </div>

                <div className="text-center mt-3">
                {!showCards && (
                    <div className="text-center mt-3">
                    <button
                        className="btn createButton"
                        onClick={() => {
                        setShowScenarioModal(true);
                        }}
                    >
                        Submit
                    </button>
                    </div>
                )}
                </div>
                {showCards && (
                <>
                    <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                    <button
                        className={` ${
                        selectedReport === "efficiency"
                            ? "btn createButton"
                            : "btn cancelButton"
                        }`}
                        onClick={() => handleReportSelect("efficiency")}
                    >
                        Efficiency Report
                    </button>

                    <button
                        className={` ${
                        selectedReport === "nonEfficiency"
                            ? "btn createButton"
                            : "btn cancelButton"
                        }`}
                        onClick={() => handleReportSelect("nonEfficiency")}
                    >
                        Non-Efficiency Report
                    </button>

                    <button
                        className={` ${
                        selectedReport === "detailed"
                            ? "btn createButton"
                            : "btn cancelButton"
                        }`}
                        onClick={() => handleReportSelect("detailed")}
                    >
                        Detailed Report
                    </button>
                    </div>
                    <div className="overflow-auto mt-3 reports-table">
                    <table
                        className="table table-bordered table-striped mt-2"
                        id="fileTable"
                    >
                        <thead className="table-dark">
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">1:</th>
                            <th scope="col">2:</th>
                            <th scope="col">3:</th>
                            <th scope="col">4:</th>
                            <th scope="col">:20:</th>
                            <th scope="col">:23B:</th>
                            <th scope="col">:32A:</th>
                            <th scope="col">:50A:</th>
                            <th scope="col">:57A:</th>
                            <th scope="col">:59:</th>
                            <th scope="col">:70:</th>
                            <th scope="col">:71A:</th>
                            <th scope="col">:72:</th>
                        </tr>
                        </thead>
                        <tbody>
                        {[
                            {
                            id: 1,
                            col1: "BANKSESSXXXX",
                            col2: "I103BANKNO2XXXXN",
                            col3: "SEPA, MNO987123654",
                            col4: "",
                            col20: "REF987123654",
                            col23B: "CRED",
                            col32A: "240303SEK3000",
                            col50A: "/9871236540 OLIVER ERICSON",
                            col57A: "BANKNO2X",
                            col59: "/5566778899 HENRY JOHNSTON",
                            col70: "HOUSE RENT",
                            col71A: "SHA",
                            col72: "/INS/SAME DAY PROCESSING",
                            },
                            {
                            id: 2,
                            col1: "BANKFIHHXXXX",
                            col2: "I103BANKDKKKXXXXN",
                            col3: "SEPA, PQR456789123",
                            col4: "",
                            col20: "REF456789123",
                            col23B: "CRED",
                            col32A: "240303DKK4000",
                            col50A: "/4567891230 KIM LAWRENCE",
                            col57A: "BANKDKKK",
                            col59: "/3344556677 ERIC ANDERSON",
                            col70: "CAR PAYMENT",
                            col71A: "BEN",
                            col72: "/INS/NORMAL CLEARANCE",
                            },
                            {
                            id: 3,
                            col1: "BANKPTPLXXXX",
                            col2: "I103BANKGRXXXXXXN",
                            col3: "SEPA, STU654321987",
                            col4: "",
                            col20: "REF654321987",
                            col23B: "CRED",
                            col32A: "240303EUR5000",
                            col50A: "/6543219870 PETER FERREIRA",
                            col57A: "BANKGRXX",
                            col59: "/2233445566 NICHOLAS PAPADOPOULOS",
                            col70: "CONTRACT PAYMENT",
                            col71A: "SHA",
                            col72: "/INS/EXPRESS HANDLING",
                            },
                            {
                            id: 4,
                            col1: "BANKESPBXXXX",
                            col2: "I103BANKITRRXXXXN",
                            col3: "SEPA, VWX321654987",
                            col4: "",
                            col20: "REF321654987",
                            col23B: "CRED",
                            col32A: "240303EUR1200",
                            col50A: "/3216549870 MARTA LOPEZ",
                            col57A: "BANKITRRX",
                            col59: "/8899001122 LUIGI BIANCHI",
                            col70: "TRAVEL EXPENSES",
                            col71A: "OUR",
                            col72: "/INS/PRIORITY TRANSFER",
                            },
                            {
                            id: 5,
                            col1: "BANKCHZZXXXX",
                            col2: "I103BANKATWWXXXXN",
                            col3: "SEPA, YZA789654123",
                            col4: "",
                            col20: "REF789654123",
                            col23B: "CRED",
                            col32A: "240303CHF3500",
                            col50A: "/7896541230 LUKAS MEIER",
                            col57A: "BANKATWW",
                            col59: "/1122446688 HELMUT SCHMIDT",
                            col70: "SAVINGS TRANSFER",
                            col71A: "SHA",
                            col72: "/INS/NEXT DAY PROCESSING",
                            },
                            {
                            id: 6,
                            col1: "BANKAUSYXXXX",
                            col2: "I103BANKNZ2XXXXXN",
                            col3: "SEPA, BCD159753468",
                            col4: "",
                            col20: "REF159753468",
                            col23B: "CRED",
                            col32A: "240303AUD2800",
                            col50A: "/1597534680 EMMA JOHNSON",
                            col57A: "BANKNZ2X",
                            col59: "/3344221100 JAMES WILSON",
                            col70: "EDUCATION LOAN REPAYMENT",
                            col71A: "BEN",
                            col72: "/INS/STANDARD PROCESSING",
                            },
                            {
                            id: 7,
                            col1: "BANKUSXXAXXX",
                            col2: "I103BANKGBXXXXN",
                            col3: "SEPA, ABC123456789",
                            col4: "",
                            col20: "REF123456789",
                            col23B: "CRED",
                            col32A: "240303USD1000",
                            col50A: "/1234567890 JOHN DOE",
                            col57A: "BANKGBXX",
                            col59: "/0987654321 JANE DOE",
                            col70: "PAYMENT FOR INVOICE 98765",
                            col71A: "SHA",
                            col72: "/INS/INSTRUCTIONS HERE",
                            },
                            {
                            id: 8,
                            col1: "BANKFRPPXXXX",
                            col2: "I103BANKDEFFXXXXN",
                            col3: "SEPA, EFG987654321",
                            col4: "",
                            col20: "REF987654321",
                            col23B: "CRED",
                            col32A: "240303EUR4500",
                            col50A: "/9876543210 JEAN DUPONT",
                            col57A: "BANKDEFFX",
                            col59: "/7766554433 HANS MULLER",
                            col70: "BUSINESS PAYMENT",
                            col71A: "OUR",
                            col72: "/INS/URGENT PROCESSING",
                            },
                            {
                            id: 9,
                            col1: "BANKSGXXAXXX",
                            col2: "I103BANKMYXXXXN",
                            col3: "SEPA, HIJ123789456",
                            col4: "",
                            col20: "REF123789456",
                            col23B: "CRED",
                            col32A: "240303SGD3200",
                            col50A: "/1237894560 TAN WEI",
                            col57A: "BANKMYXX",
                            col59: "/6655443322 ABDUL RAHMAN",
                            col70: "LOAN PAYMENT",
                            col71A: "SHA",
                            col72: "/INS/REGULAR CLEARANCE",
                            },
                            {
                            id: 10,
                            col1: "BANKCNXXBXXX",
                            col2: "I103BANKHKXXXXN",
                            col3: "SEPA, KLM987321654",
                            col4: "",
                            col20: "REF987321654",
                            col23B: "CRED",
                            col32A: "240303CNY6000",
                            col50A: "/9873216540 LI WEI",
                            col57A: "BANKHKXX",
                            col59: "/5544332211 WONG CHI",
                            col70: "REAL ESTATE PAYMENT",
                            col71A: "BEN",
                            col72: "/INS/STANDARD PROCEDURE",
                            },
                        ]
                            .slice(
                            (currentPage - 1) * itemsPerPage,
                            currentPage * itemsPerPage
                            )
                            .map((row, index) => (
                            <tr key={row.id}>
                                <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                                </td>
                                <td>{row.col1}</td>
                                <td>{row.col2}</td>
                                <td>{row.col3}</td>
                                <td>{row.col4}</td>
                                <td>{row.col20}</td>
                                <td>{row.col23B}</td>
                                <td>{row.col32A}</td>
                                <td>{row.col50A}</td>
                                <td>{row.col57A}</td>
                                <td>{row.col59}</td>
                                <td>{row.col70}</td>
                                <td>{row.col71A}</td>
                                <td>{row.col72}</td>
                            </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex gap-3">
                        <button
                        className="btn createButton"
                        onClick={handleExportXLSX}
                        >
                        Download
                        </button>
                        <button
                        className="btn cancelButton"
                        onClick={() => setShowCards(false)}
                        >
                        Reset
                        </button>
                    </div>
                    <div className="pagination">
                        <Button
                        variant="outlined"
                        size="small"
                        onClick={handleFirstPage}
                        disabled={currentPage === 1}
                        className="me-2"
                        >
                        First
                        </Button>
                        <Button
                        variant="outlined"
                        size="small"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className="me-2"
                        >
                        Prev
                        </Button>
                        <span className="mx-2 align-self-center">
                        Page {currentPage} of {Math.ceil(10 / itemsPerPage)}
                        </span>
                        <Button
                        variant="outlined"
                        size="small"
                        onClick={handleNextPage}
                        disabled={currentPage === Math.ceil(10 / itemsPerPage)}
                        className="me-2"
                        >
                        Next
                        </Button>
                        <Button
                        variant="outlined"
                        size="small"
                        onClick={handleLastPage}
                        disabled={currentPage === Math.ceil(10 / itemsPerPage)}
                        >
                        Last
                        </Button>
                    </div>
                    </div>
                </>
                )}
            </div>
        </div>

        {showScenarioModal && (
          <div className="modal-overlay">
            <div
              className="modal-container"
              style={{ maxWidth: "90%", width: "90%" }}
            >
              <div className="row justify-content-center align-items-center">
                <div className="col-12 d-flex justify-content-between align-items-center">
                  {!selectedScenario ? (
                    <h5 className="mt-4">Efficiency Report</h5>
                  ) : (
                    <div></div>
                  )}
                  <button
                    className="border-0 bg-transparent"
                    onClick={handleCloseScenarioModal}
                  >
                    <MdClose color="black" fontSize={20} />
                  </button>
                </div>
              </div>

              {!selectedScenario ? (
                // Summary Table
                <div className="overflow-auto mt-3 remove-box-shadow">
                  <table className="table table-bordered table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>Scenario Name</th>
                        <th>Total Records</th>
                        <th>Hit Count</th>
                        <th>Hit Percentage</th>
                        <th>Unhit Count</th>
                        <th>Unhit Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scenarioSummaryData
                        .slice(
                          (scenarioPage - 1) * itemsPerPage,
                          scenarioPage * itemsPerPage
                        )
                        .map((scenario) => (
                          <tr key={scenario.id} style={{ cursor: "pointer" }}>
                            <td onClick={() => handleScenarioClick(scenario)}>
                              {scenario.scenarioName}
                            </td>
                            <td onClick={() => handleScenarioClick(scenario)}>
                              {scenario.totalRecords}
                            </td>
                            <td
                              className="table-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScenarioClick(scenario, "hits");
                              }}
                            >
                              {scenario.hitCount}
                            </td>
                            <td onClick={() => handleScenarioClick(scenario)}>
                              {scenario.hitPercentage}%
                            </td>
                            <td
                              className="table-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScenarioClick(scenario, "unhits");
                              }}
                            >
                              {scenario.unhitCount}
                            </td>
                            <td onClick={() => handleScenarioClick(scenario)}>
                              {scenario.unhitPercentage}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-end mt-3">
                    <div className="pagination">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleScenarioFirstPage}
                        disabled={scenarioPage === 1}
                        className="me-2"
                      >
                        First
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleScenarioPrevPage}
                        disabled={scenarioPage === 1}
                        className="me-2"
                      >
                        Prev
                      </Button>
                      <span className="mx-2 align-self-center">
                        Page {scenarioPage} of{" "}
                        {Math.ceil(scenarioSummaryData.length / itemsPerPage)}
                      </span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleScenarioNextPage}
                        disabled={
                          scenarioPage ===
                          Math.ceil(scenarioSummaryData.length / itemsPerPage)
                        }
                        className="me-2"
                      >
                        Next
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleScenarioLastPage}
                        disabled={
                          scenarioPage ===
                          Math.ceil(scenarioSummaryData.length / itemsPerPage)
                        }
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                  {/* Download Button */}
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      className="btn createButton"
                      onClick={() => handleDownload()} // Added missing onClick handler
                    >
                      Download
                    </button>
                  </div>
                </div>
              ) : selectedScenario && !selectedHit && !selectedUnhit ? (
                // Scenario Details - Showing either Hits or Unhits based on user click
                <div className="mt-3">
                  <h5 className="mb-4">{selectedScenario.scenarioName}</h5>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdSearch />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  {/* Show Hits Table */}
                  {viewType === "hits" && (
                    <>
                      <h6>Hits ({filteredHits.length})</h6>
                      <table className="table table-bordered table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th>Name</th>
                            <th>Processed Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHits
                            .slice(
                              (hitPage - 1) * itemsPerPage,
                              hitPage * itemsPerPage
                            )
                            .map((hit, index) => (
                              <tr
                                key={index}
                                onClick={() => handleHitClick(hit)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>{hit.name}</td>
                                <td>{hit.processedName}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-end mt-3">
                        <div className="pagination">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleHitFirstPage}
                            disabled={hitPage === 1}
                            className="me-2"
                          >
                            First
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleHitPrevPage}
                            disabled={hitPage === 1}
                            className="me-2"
                          >
                            Prev
                          </Button>
                          <span className="mx-2 align-self-center">
                            Page {hitPage} of{" "}
                            {Math.ceil(
                              selectedScenario.details.hits.length /
                                itemsPerPage
                            )}
                          </span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleHitNextPage}
                            disabled={
                              hitPage ===
                              Math.ceil(
                                selectedScenario.details.hits.length /
                                  itemsPerPage
                              )
                            }
                            className="me-2"
                          >
                            Next
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleHitLastPage}
                            disabled={
                              hitPage ===
                              Math.ceil(
                                selectedScenario.details.hits.length /
                                  itemsPerPage
                              )
                            }
                          >
                            Last
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Show Unhits Table */}
                  {viewType === "unhits" && (
                    <>
                      <h6>Unhits ({selectedScenario.unhitCount})</h6>
                      <table className="table table-bordered table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th>Name</th>
                            <th>Processed Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedScenario.details.unhits
                            .slice(
                              (unhitPage - 1) * itemsPerPage,
                              unhitPage * itemsPerPage
                            )
                            .map((unhit, index) => (
                              <tr key={index} style={{ cursor: "pointer" }}>
                                <td>{unhit.name}</td>
                                <td>{unhit.processedName}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-end mt-3">
                        <div className="pagination">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUnhitFirstPage}
                            disabled={unhitPage === 1}
                            className="me-2"
                          >
                            First
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUnhitPrevPage}
                            disabled={unhitPage === 1}
                            className="me-2"
                          >
                            Prev
                          </Button>
                          <span className="mx-2 align-self-center">
                            Page {unhitPage} of{" "}
                            {Math.ceil(
                              selectedScenario.details.unhits.length /
                                itemsPerPage
                            )}
                          </span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUnhitNextPage}
                            disabled={
                              unhitPage ===
                              Math.ceil(
                                selectedScenario.details.unhits.length /
                                  itemsPerPage
                              )
                            }
                            className="me-2"
                          >
                            Next
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUnhitLastPage}
                            disabled={
                              unhitPage ===
                              Math.ceil(
                                selectedScenario.details.unhits.length /
                                  itemsPerPage
                              )
                            }
                          >
                            Last
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-center mt-3">
                    <button
                      className="btn cancelButton"
                      onClick={() => setSelectedScenario(null)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : selectedHit ? (
                // Detail view for selected hit with violations
                <div className="mt-3">
                  <h5 className="mb-4">Hit Details: {selectedHit.name}</h5>
                  <div className="card p-3">
                    <div className="mb-3">
                      <strong>Name:</strong> {selectedHit.name}
                    </div>
                    <div className="mb-3">
                      <strong>Processed Name:</strong>{" "}
                      {selectedHit.processedName}
                    </div>
                    <div>
                      <strong>Violations:</strong>
                      <ul className="mt-2">
                        {selectedHit.violations &&
                          selectedHit.violations.map((violation, index) => (
                            <li key={index}>{violation}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      className="btn cancelButton me-3"
                      onClick={() => setSelectedHit(null)}
                    >
                      Back
                    </button>
                    <button
                      className="btn createButton"
                      onClick={() => {
                        setSelectedHit(null);
                        setSelectedScenario(null);
                      }}
                    >
                      Back to Summary
                    </button>
                  </div>
                </div>
              ) : selectedUnhit ? (
                // Detail view for selected unhit with variations
                <div className="mt-3">
                  <h5 className="mb-4">Unhit Details: {selectedUnhit.name}</h5>
                  <div className="card p-3">
                    <div className="mb-3">
                      <strong>Name:</strong> {selectedUnhit.name}
                    </div>
                    <div className="mb-3">
                      <strong>Processed Name:</strong>{" "}
                      {selectedUnhit.processedName}
                    </div>
                    <div>
                      <strong>Variations:</strong>
                      <ul className="mt-2">
                        {selectedUnhit.variations &&
                          selectedUnhit.variations.map((variation, index) => (
                            <li key={index}>{variation}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      className="btn cancelButton"
                      onClick={() => setSelectedUnhit(null)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3">
                  <h5 className="mb-4">No Data Available</h5>
                </div>
              )}
            </div>
          </div>
        )}
        {viewFilesModal && (
          <div className="modal-overlay">
            <div
              className="modal-container bg-white"
              style={{
                maxWidth: "90%",
                width: "90%",
                maxHeight: "90vh", // Set maximum height to 90% of viewport height
                height: "auto",
                overflowY: "auto", // Enable vertical scrolling for the entire modal
                display: "flex",
                flexDirection: "column"
              }}
            >
              {/* Header Section */}
              <div className="row justify-content-center align-items-center bg-white z-index-1">
                <div className="col-12 d-flex justify-content-between align-items-center py-3 border-bottom">
                  <h5 className="m-0 px-2">View Files</h5>
                  <button
                    className="border-0 bg-transparent"
                    onClick={() => {
                      setViewFilesModal(false);
                      setModalCurrentPage(1);
                    }}
                  >
                    <MdClose color="black" fontSize={20} />
                  </button>
                </div>
              </div>
        
              {/* Main Content with Single Scroll */}
              <div className="modal-body p-3">
                {/* Table Section (File List) - Always visible */}
                <div className="mb-4">
                  <h6 className="mb-3">File List</h6>
                  <div className="table-responsive border rounded bg-white shadow-sm">
                    <table className="table table-bordered table-striped mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>#</th>
                          <th>File Name</th>
                          <th>Load Date</th>
                          <th>Load Time</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staticFileList
                          .slice(
                            (modalCurrentPage - 1) * modalItemsPerPage,
                            modalCurrentPage * modalItemsPerPage
                          )
                          .map((row, index) => (
                            <tr key={row.id}>
                              <td>
                                {(modalCurrentPage - 1) * modalItemsPerPage +
                                  index +
                                  1}
                              </td>
                              <td>{row.fileName}</td>
                              <td>{row.loadDate}</td>
                              <td>{row.loadTime}</td>
                              <td className="text-center">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<MdVisibility />}
                                  onClick={() => {
                                    setShowScenarioModal(true);
                                    setViewFilesModal(false);
                                }}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
        
                  {/* Pagination Controls for File Table */}
                    <div className="d-flex justify-content-end mt-3">
                        <div className="pagination">
                            <Button variant="outlined" size="small" onClick={handleModalFirstPage} disabled={modalCurrentPage === 1} className="me-2">First</Button>
                            <Button variant="outlined" size="small" onClick={handleModalPrevPage} disabled={modalCurrentPage === 1} className="me-2">Prev</Button>
                            <span className="mx-2 align-self-center">Page {modalCurrentPage} of {Math.ceil(staticFileList.length / modalItemsPerPage)}</span>
                            <Button variant="outlined" size="small" onClick={handleModalNextPage} disabled={modalCurrentPage === Math.ceil(staticFileList.length / modalItemsPerPage)} className="me-2">Next</Button>
                            <Button variant="outlined" size="small" onClick={handleModalLastPage} disabled={modalCurrentPage === Math.ceil(staticFileList.length / modalItemsPerPage)}>Last</Button>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
}
export default Report;
